// Контрол изотермы для слоя SST: тумблер + ползунок градуса (шаг 0.5 °C), плюс
// два ДОЧЕРНИХ инструмента — поверхностная изотерма и смежные линии.
//
// Значения меняются мгновенно — линии пересчитываются в браузере по уже
// скачанной сетке, без запроса в сеть (см. useTemperatureTools). Исключение
// одно: включение «Поверхности» на глубине ≠ 0 просит вторую сетку (0 м) — это
// другое поле, взять его неоткуда.
//
// Дочерние тумблеры стоят ВНУТРИ блока и вместе с ним исчезают: смежные линии
// отбиваются ОТ основной изотермы, а поверхностная читается в паре с ней. Без
// основной они остались бы линиями, которые нечем выключить.
//
// Каркас раздела (заголовок + образец цвета + тумблер) — общий с «Фронтами» и
// «Волнением», см. OverlaySection.
import { ISOTHERM_STEP } from '../map/isotherms'
import { ISO_SPREAD_MAX, ISO_SPREAD_MIN, ISO_SPREAD_SLIDER } from '../map/isothermFamily'
import { formatNumber, useLang, useT } from '../i18n'
import { OverlaySection } from './OverlaySection'
import { ThermalCheatSheet } from './ThermalCheatSheet'
import { isothermSurfaceSwatch, isothermSwatch, swatchBackground } from './overlaySwatch'

interface Props {
  enabled: boolean
  value: number
  min: number
  max: number
  onToggle: (on: boolean) => void
  onChange: (value: number) => void
  /** Градус поверхностной изотермы; null — её тумблер выключен. */
  surface: number | null
  surfaceDefault: number
  onSurfaceToggle: (on: boolean) => void
  onSurfaceChange: (value: number) => void
  /** Шаг смежных изотерм; null — тумблер выключен. */
  spread: number | null
  spreadDefault: number
  onSpreadToggle: (on: boolean) => void
  onSpreadChange: (value: number) => void
  /** Дата вида — от неё месяц шпаргалки модели; не задана — шпаргалки нет. */
  hintsDate?: string
  /** Выбранный вид — его строка в шпаргалке подсвечена. */
  species?: number | null
}

/** Ряд «тумблер + подпись» дочернего инструмента. Форма та же, что у галки
 *  «суда без категорий»: внутри блока это не самостоятельный раздел. */
function ChildToggle({
  on,
  label,
  swatch,
  onToggle,
}: {
  on: boolean
  label: string
  swatch?: string
  onToggle: (on: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onToggle(!on)}
      className="flex w-full items-center gap-2 text-left text-[11px] text-mist"
    >
      <span
        className={
          'relative h-4 w-7 shrink-0 rounded-full transition-colors ' + (on ? 'bg-sonar' : 'bg-hull')
        }
      >
        <span
          className="absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all"
          style={{ left: on ? 14 : 2 }}
        />
      </span>
      {label}
      {swatch && (
        // Образец цвета — по нему на карте отличают вторую линию от основной.
        <span
          aria-hidden
          className={'ml-auto h-2 w-10 rounded-full transition-opacity ' + (on ? '' : 'opacity-40')}
          style={{ background: swatch }}
        />
      )}
    </button>
  )
}

export function IsothermControl({
  enabled,
  value,
  min,
  max,
  onToggle,
  onChange,
  surface,
  surfaceDefault,
  onSurfaceToggle,
  onSurfaceChange,
  spread,
  spreadDefault,
  onSpreadToggle,
  onSpreadChange,
  hintsDate,
  species = null,
}: Props) {
  const t = useT()
  const { lang } = useLang()
  const fmt = (v: number) => formatNumber(lang, v, 1)
  return (
    <OverlaySection
      title={t('maps.isotherm.title')}
      hint="isotherm"
      swatch={isothermSwatch()}
      enabled={enabled}
      toggleLabel={t('maps.isotherm.toggle')}
      onToggle={onToggle}
    >
      <div className="mb-0.5 flex items-baseline gap-1">
        <span className="tnum text-2xl font-bold leading-none text-foam">{fmt(value)}</span>
        <span className="text-xs font-medium text-sonar">{t('units.degc')}</span>
      </div>
      <div className="mb-2 text-[11px] text-mist">{t('maps.isotherm.degree')}</div>
      <input
        type="range"
        min={min}
        max={max}
        step={ISOTHERM_STEP}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        aria-label={t('maps.isotherm.degree')}
        className="isotherm-range w-full"
      />
      <div className="mt-1 flex justify-between tnum text-[10px] text-mist">
        <span>{fmt(min)}</span>
        <span>{fmt(max)}</span>
      </div>

      <div className="mt-3 space-y-2 border-t border-hull pt-2.5">
        <ChildToggle
          on={surface != null}
          label={t('maps.isotherm.surface.toggle')}
          swatch={swatchBackground(isothermSurfaceSwatch())}
          onToggle={on => onSurfaceToggle(on)}
        />
        {surface != null && (
          <div>
            <div className="mb-1 flex items-baseline gap-1">
              <span className="tnum text-lg font-bold leading-none text-foam">{fmt(surface)}</span>
              <span className="text-[11px] font-medium text-sonar">{t('units.degc')}</span>
              <span className="ml-auto text-[10px] text-faint">
                {t('maps.isotherm.surface.degree')}
              </span>
            </div>
            <input
              type="range"
              min={min}
              max={max}
              step={ISOTHERM_STEP}
              value={surface ?? surfaceDefault}
              onChange={e => onSurfaceChange(Number(e.target.value))}
              aria-label={t('maps.isotherm.surface.degree')}
              className="isotherm-range w-full"
            />
          </div>
        )}

        <ChildToggle
          on={spread != null}
          label={t('maps.isotherm.spread.toggle')}
          onToggle={on => onSpreadToggle(on)}
        />
        {spread != null && (
          <div>
            <div className="mb-1 flex items-baseline gap-1">
              <span className="tnum text-lg font-bold leading-none text-foam">{fmt(spread)}</span>
              <span className="text-[11px] font-medium text-sonar">{t('units.degc')}</span>
              <span className="ml-auto text-[10px] text-faint">
                {t('maps.isotherm.spread.step')}
              </span>
            </div>
            <input
              type="range"
              min={ISO_SPREAD_MIN}
              max={ISO_SPREAD_MAX}
              step={ISO_SPREAD_SLIDER}
              value={spread ?? spreadDefault}
              onChange={e => onSpreadChange(Number(e.target.value))}
              aria-label={t('maps.isotherm.spread.step')}
              className="isotherm-range w-full"
            />
            <div className="mt-1 flex justify-between tnum text-[10px] text-mist">
              <span>{fmt(ISO_SPREAD_MIN)}</span>
              <span>{fmt(ISO_SPREAD_MAX)}</span>
            </div>
          </div>
        )}

        {/* Шпаргалка модели: какие градусы сейчас в сезонной модели пелагиков.
            Число из неё ставит ОСНОВНУЮ изотерму — ту, что на глубине Контекста. */}
        {hintsDate && (
          <ThermalCheatSheet date={hintsDate} species={species} min={min} max={max} onPick={onChange} />
        )}
      </div>
    </OverlaySection>
  )
}
